import axios from '@/lib/axios';

export interface SkillStats {
    id: string;
    name: string;
    questionsAnswered: number;
    totalQuestions: number;
    masteryStatus: 'mastered' | 'in-progress' | 'not-started';
    masteryPercentage: number;
    videoLinks?: {
        id?: string;
        url: string;
        caption: string;
        likes?: number;
        dislikes?: number;
        userVote?: number;
    }[];
}

export interface DomainStats {
    id: string;
    name: string;
    description: string;
    skills: SkillStats[];
}

export interface FilterCounts {
    providers: { [key: string]: number };
    status: { [key: string]: number };
    difficulty: { [key: string]: number };
}

export interface PracticeDashboardResponse {
    filters: FilterCounts;
    domains: DomainStats[];
    providerNames?: { [key: string]: string };
}

export interface QuestionUserContext {
    isMarked: boolean;
    isMastered?: boolean;
    attemptCount: number;
    lastAttemptAnswer?: string;
    lastAttemptIsCorrect?: boolean;
}

export interface QuestionResponse {
    id: number;
    questionId: string;
    providerCode: string;
    providerName?: string;
    domainName?: string;
    skillName?: string;
    difficulty?: string;
    stimulus?: string;
    stem: string;
    mcqOptions?: { [key: string]: string };
    mcqAnswer?: string;
    questionType: string;
    questionCategory?: string;
    userContext?: QuestionUserContext;
    explanation?: string;
    voiceNoteUrl?: string;
    solutionImageUrl?: string; // legacy single image, kept for backward compat
    solutionImages?: { id: string; url: string; caption?: string; order?: number }[]; // new array
    tutorialLink?: string;
    reviewerName?: string;
    reviewerId?: number | null;
    // Tutorial upload tracking
    tutorialUploadedById?: number;
    tutorialUploadedByEmail?: string;
    tutorialUploadedByName?: string;
    tutorialUploadedAt?: string;
    // Voice note upload tracking
    voiceNoteUploadedById?: number;
    voiceNoteUploadedByEmail?: string;
    voiceNoteUploadedByName?: string;
    voiceNoteUploadedAt?: string;

    feedbackCount?: number;
}

export interface PracticeSessionResponse {
    skillId: string;
    sessionId: number;
    questionIds: number[];  // Changed from questions to just IDs
    total: number;  // Total number of questions
}

export interface CheckAnswerRequest {
    questionId: number;
    userAnswer: string;
    timeTaken: number;
}

export interface CheckAnswerResponse {
    isCorrect: boolean;
    correctAnswer: string;
    explanation?: string;
    isMastered?: boolean;
}

export interface ToggleMarkResponse {
    marked: boolean;
    message: string;
}


export interface SessionResultResponse {
    sessionId: number;
    skillId: string;
    totalQuestions: number;
    correctAnswers: number;
    accuracy: number;
    totalTimeSeconds: number;
    averageTimePerQuestion: number;
    completionDate: string;
}

export interface DashboardFilters {
    providers?: string[];
    status?: string[];
    difficulty?: string[];
    session_id?: string;
    subject?: string;
}

export interface ReportResponse {
    status: string;
    message: string;
}

export interface QuestionAttempt {
    id: number;
    selectedAnswer: string;
    isCorrect: boolean;
    attemptedAt: string;
    timeTakenSeconds: number;
}

export interface UserFeedbackItem {
    id: number;
    user_id: number;
    question_id?: number | null;
    question_stem?: string | null;
    question_domain?: string | null;
    question_skill?: string | null;
    content: string;
    created_at: string;
    likes: number;
    dislikes: number;
    net_votes: number;
    user_name?: string;
    user_avatar?: string | null;
    image_url?: string | null;
    voice_url?: string | null;
    comments_count: number;
    user_vote?: number;
    is_solved?: boolean;
    is_active?: boolean;
}

export interface UserFeedbackResponse {
    feedbacks: UserFeedbackItem[];
    total: number;
}

export const practiceService = {
    getDashboard: async (filters?: DashboardFilters): Promise<PracticeDashboardResponse> => {
        const response = await axios.get('/practice/dashboard', {
            params: filters,
            paramsSerializer: (params) => {
                const searchParams = new URLSearchParams();
                Object.keys(params).forEach(key => {
                    const value = params[key as keyof DashboardFilters];
                    if (Array.isArray(value)) {
                        value.forEach(val => searchParams.append(key, val));
                    } else if (value !== undefined && value !== null) {
                        searchParams.append(key, value as string);
                    }
                });
                return searchParams.toString();
            }
        });
        return response.data;
    },

    getSession: async (skillId: string, filters?: DashboardFilters, sessionId?: number): Promise<PracticeSessionResponse> => {
        const params = { ...filters };
        if (sessionId) {
            (params as any).session_id = sessionId;
        }

        const response = await axios.get(`/practice/session/${skillId}`, {
            params,
            paramsSerializer: (params) => {
                const searchParams = new URLSearchParams();
                Object.keys(params).forEach(key => {
                    const value = params[key as keyof DashboardFilters];
                    if (Array.isArray(value)) {
                        value.forEach(val => searchParams.append(key, val));
                    } else if (value !== undefined && value !== null) {
                        searchParams.append(key, value as string);
                    }
                });
                return searchParams.toString();
            }
        });
        return response.data;
    },

    getQuestionsBatch: async (questionIds: (string | number)[]): Promise<QuestionResponse[]> => {
        const response = await axios.post('/practice/questions/batch', questionIds);
        return response.data;
    },

    getQuestionSolution: async (questionId: number): Promise<QuestionResponse> => {
        const response = await axios.get(`/practice/questions/${questionId}/solution`);
        return response.data;
    },



    checkAnswer: async (sessionId: number, data: CheckAnswerRequest): Promise<CheckAnswerResponse> => {
        const response = await axios.post(`/practice/session/${sessionId}/check`, data);
        return response.data;
    },

    toggleMark: async (sessionId: number, questionId: number, marked: boolean): Promise<ToggleMarkResponse> => {
        const response = await axios.post(`/practice/session/${sessionId}/questions/${questionId}/mark`, { marked });
        return response.data;
    },

    completeSession: async (sessionId: number): Promise<SessionResultResponse> => {
        const response = await axios.post(`/practice/session/${sessionId}/complete`);
        return response.data;
    },

    getSessionResult: async (sessionId: number): Promise<SessionResultResponse> => {
        const response = await axios.get(`/practice/session/${sessionId}/result`);
        return response.data;
    },

    reportQuestion: async (questionId: number, reportType: string, comment: string): Promise<ReportResponse> => {
        const response = await axios.post(`/practice/questions/${questionId}/report`, {
            issue_topic: reportType,
            description: comment
        });
        return response.data;
    },

    createSessionFromIds: async (questionIds: (string | number)[], skillSlug: string = "mistakes-practice"): Promise<PracticeSessionResponse> => {
        const response = await axios.post('/practice/session/from-ids', {
            questionIds,
            skillSlug
        });
        return response.data;
    },

    getSkillVideos: async (skillSlug: string) => {
        const response = await axios.get(`/practice/skills/${skillSlug}/videos`);
        return response.data;
    },

    voteVideo: async (skillSlug: string, videoId: string, voteType: number) => {
        const response = await axios.post(`/practice/skills/${skillSlug}/videos/${videoId}/vote`, {
            vote_type: voteType
        });
        return response.data;
    },

    getQuestionAttempts: async (questionId: number): Promise<QuestionAttempt[]> => {
        const response = await axios.get(`/practice/questions/${questionId}/attempts`);
        return response.data;
    },

    getUserFeedback: async (filterType: 'all' | 'doubts' | 'discussions' = 'all'): Promise<UserFeedbackResponse> => {
        const response = await axios.get('/practice/my-feedback', {
            params: { filter_type: filterType }
        });
        return response.data;
    },

    createDiscussion: async (content: string, imageUrl?: string | null, voiceUrl?: string | null): Promise<any> => {
        const response = await axios.post('/practice/discussion/create', {
            content,
            question_id: null,
            image_url: imageUrl,
            voice_url: voiceUrl
        });
        return response.data;
    },

    voteFeedback: async (feedbackId: number, voteType: number): Promise<void> => {
        await axios.post(`/practice/feedback/${feedbackId}/vote`, {
            vote_type: voteType
        });
    },

    deleteFeedback: async (feedbackId: number): Promise<void> => {
        await axios.delete(`/practice/feedback/${feedbackId}`);
    },

    toggleFeedbackActive: async (feedbackId: number): Promise<{ is_active: boolean }> => {
        const response = await axios.post(`/practice/feedback/${feedbackId}/toggle-active`);
        return response.data;
    },

    markFeedbackAsSolved: async (feedbackId: number): Promise<{ is_solved: boolean }> => {
        const response = await axios.post(`/practice/feedbacks/${feedbackId}/solve`);
        return response.data;
    },

    markCommentAsSolved: async (commentId: number): Promise<{ is_solved: boolean }> => {
        const response = await axios.post(`/practice/comments/${commentId}/solve`);
        return response.data;
    },

    // Question Discussion methods
    submitQuestionFeedback: async (questionId: number, content: string, imageUrl?: string | null, voiceUrl?: string | null): Promise<any> => {
        const response = await axios.post(`/practice/questions/${questionId}/feedback`, {
            content,
            question_id: questionId,
            image_url: imageUrl,
            voice_url: voiceUrl
        });
        return response.data;
    },

    getQuestionDiscussion: async (questionId: number): Promise<QuestionDiscussionResponse> => {
        const response = await axios.get(`/practice/questions/${questionId}/discussion`);
        return response.data;
    },

    createFeedbackComment: async (feedbackId: number, content: string, parentId?: number, imageUrl?: string | null, voiceUrl?: string | null): Promise<CommentResponse> => {
        const response = await axios.post(`/practice/feedback/${feedbackId}/comments`, {
            content,
            parent_id: parentId,
            image_url: imageUrl,
            voice_url: voiceUrl
        });
        return response.data;
    },

    voteFeedbackComment: async (commentId: number, voteType: number): Promise<void> => {
        await axios.post(`/practice/feedback/comments/${commentId}/vote`, {
            vote_type: voteType
        });
    },

    deleteFeedbackComment: async (commentId: number): Promise<void> => {
        await axios.delete(`/practice/feedback/comments/${commentId}`);
    },

    toggleCommentActive: async (commentId: number): Promise<{ is_active: boolean }> => {
        const response = await axios.post(`/practice/comments/${commentId}/toggle-active`);
        return response.data;
    },

    getFeedbackDiscussion: async (feedbackId: number): Promise<FeedbackWithComments> => {
        const response = await axios.get(`/practice/feedback/${feedbackId}`);
        return response.data;
    },

    unlockLearnAccess: async (): Promise<any> => {
        const response = await axios.post('/payment/unlock-learn');
        return response.data;
    }
};

// Discussion interfaces
export interface CommentResponse {
    id: number
    user_name: string
    user_avatar?: string | null
    user_id: number
    content: string
    image_url?: string | null
    voice_url?: string | null
    created_at: string
    upvotes: number
    downvotes: number
    net_votes: number
    user_vote: number
    is_solved?: boolean;
    is_active?: boolean;
    replies: CommentResponse[]
}

export interface FeedbackWithComments {
    id: number
    user_name: string
    user_avatar?: string | null
    user_id: number
    content: string
    image_url?: string | null
    voice_url?: string | null
    created_at: string
    likes: number
    dislikes: number
    net_votes: number
    user_vote: number
    is_solved?: boolean;
    is_active?: boolean;
    comments: CommentResponse[]
    question_id?: number | null
}

export interface QuestionDiscussionResponse {
    question: QuestionResponse
    feedbacks: FeedbackWithComments[]
    total_feedbacks: number
    total_comments: number
    attempted_count: number
    mastered_count: number
}
